import { useState } from 'react';
import { useSearchClients, useCreateClient } from '@/hooks/useClients';
import { HttpError } from '@/lib/api';
import { useUiStore } from '@/stores/uiStore';
import { useAuthStore } from '@/stores/authStore';

export default function ClientsPage() {
  const [query, setQuery] = useState('');
  const { data: clients, isLoading, isError } = useSearchClients(query);
  const [showNewClient, setShowNewClient] = useState(false);
  const openNewRdv = useUiStore((s) => s.openNewRdvModal);
  const role = useAuthStore((s) => s.user?.role);
  const canCreate = role === 'admin' || role === 'reception' || role === 'atelier';

  return (
    <div className="p-6 space-y-5">
      <header className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-text-primary">Clients</h1>
          <p className="text-xs text-text-muted font-mono mt-0.5">
            {clients ? `${clients.length} client${clients.length > 1 ? 's' : ''}` : 'Chargement…'}
          </p>
        </div>
        {canCreate && (
          <button
            type="button"
            onClick={() => setShowNewClient(true)}
            className="btn btn-primary btn-sm"
          >
            + Nouveau client
          </button>
        )}
      </header>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher par nom, prénom, téléphone, email…"
            className="input pl-9"
          />
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted text-sm">
            🔍
          </span>
        </div>
      </div>

      {showNewClient && canCreate && (
        <NewClientForm onClose={() => setShowNewClient(false)} />
      )}

      {isError && (
        <div className="px-4 py-3 rounded-md bg-status-annule-bg text-status-annule-text text-sm">
          Erreur lors du chargement des clients
        </div>
      )}

      {isLoading ? (
        <div className="text-text-muted text-sm">Chargement…</div>
      ) : !clients || clients.length === 0 ? (
        <div className="text-center py-16 bg-surface-white border border-border-light rounded-lg">
          <p className="text-text-muted text-sm mb-3">
            {query ? `Aucun client ne correspond à "${query}"` : 'Aucun client enregistré'}
          </p>
          {canCreate && !query && (
            <button
              type="button"
              onClick={() => setShowNewClient(true)}
              className="btn btn-primary btn-sm"
            >
              + Créer le premier client
            </button>
          )}
        </div>
      ) : (
        <section className="bg-surface-white border border-border-light rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-bg">
              <tr className="text-xs uppercase tracking-wider text-text-muted">
                <th className="text-left px-4 py-2.5">Client</th>
                <th className="text-left px-4 py-2.5">Téléphone</th>
                <th className="text-left px-4 py-2.5">Email</th>
                <th className="text-left px-4 py-2.5">Motos</th>
                <th className="text-right px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {clients.map((c) => (
                <tr key={c.id} className="border-t border-surface-divider hover:bg-surface-hover">
                  <td className="px-4 py-3 font-medium text-text-primary">
                    {c.prenom} {c.nom}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-text-secondary">
                    <a
                      href={`tel:${c.telephone}`}
                      className="hover:underline cursor-pointer"
                    >
                      {c.telephone}
                    </a>
                  </td>
                  <td className="px-4 py-3 text-text-secondary text-xs">
                    {c.email ?? <span className="text-text-disabled italic">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    {c.motos.length === 0 ? (
                      <span className="text-text-disabled text-xs italic">—</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {c.motos.map((m) => {
                          const tooltip = [
                            `${m.modele} · VIN ${m.vin}`,
                            m.annee ? `Année ${m.annee}` : null,
                            m.kilometrage ? `${m.kilometrage.toLocaleString('fr-FR')} km` : null,
                          ]
                            .filter(Boolean)
                            .join(' · ');
                          return (
                            <span
                              key={m.id}
                              className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-sm bg-surface-active text-xs"
                              title={tooltip}
                            >
                              <span className="font-medium">{m.modele}</span>
                              <span className="font-mono text-text-muted">{m.vin}</span>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canCreate && (
                      <button
                        type="button"
                        onClick={() => openNewRdv()}
                        className="text-xs text-ktm-orange hover:underline"
                      >
                        + RDV
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}

function NewClientForm({ onClose }: { onClose: () => void }) {
  const createClient = useCreateClient();
  const [form, setForm] = useState({ prenom: '', nom: '', telephone: '+212', email: '' });

  async function submit() {
    try {
      await createClient.mutateAsync({
        prenom: form.prenom,
        nom: form.nom,
        telephone: form.telephone,
        email: form.email || undefined,
      });
      onClose();
    } catch {
      // shown below
    }
  }

  return (
    <section className="bg-surface-white border border-border-light rounded-lg p-5 space-y-3">
      <h2 className="text-xs font-semibold uppercase tracking-wider text-text-primary">
        Nouveau client
      </h2>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="input-label">Prénom</label>
          <input
            type="text"
            value={form.prenom}
            onChange={(e) => setForm({ ...form, prenom: e.target.value })}
            className="input"
          />
        </div>
        <div className="space-y-1">
          <label className="input-label">Nom</label>
          <input
            type="text"
            value={form.nom}
            onChange={(e) => setForm({ ...form, nom: e.target.value })}
            className="input"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="input-label">Téléphone</label>
          <input
            type="text"
            value={form.telephone}
            onChange={(e) => setForm({ ...form, telephone: e.target.value })}
            placeholder="+212XXXXXXXXX"
            className="input"
          />
        </div>
        <div className="space-y-1">
          <label className="input-label">Email (optionnel)</label>
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="input"
          />
        </div>
      </div>
      {createClient.isError && (
        <div className="text-xs text-status-annule-text">
          {createClient.error instanceof HttpError
            ? createClient.error.payload.message
            : 'Erreur création'}
        </div>
      )}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">
          Annuler
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={createClient.isPending || !form.prenom || !form.nom || !form.telephone}
          className="btn btn-primary btn-sm"
        >
          {createClient.isPending ? '…' : 'Créer'}
        </button>
      </div>
    </section>
  );
}
